import { createContext, type ReactNode, useContext, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiSignOut, useGetMe } from '@/api/me'
import type { UserGetMeResponse } from '@/api/me/type'
import useLocalStorage from '@/hooks/useLocalStorage'
import { useNavigate } from 'react-router'
import pagePath from '@/utils/pagePath'
import { identifyUser } from '@/libs/log'
import { type AuthRole } from '@collable/ui'

export type AuthContextType = {
  isLoggedIn: boolean | undefined
  me: UserGetMeResponse | undefined
  role: AuthRole | undefined
  actions: {
    signOut: () => void
    reloadMe: () => void
  }
}

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: undefined,
  me: undefined,
  role: undefined,
  actions: {
    signOut: () => {},
    reloadMe: () => {},
  },
})

export const useAuthContext = () => useContext(AuthContext)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [me, setMe] = useState<UserGetMeResponse | null>()
  const [role, setRole] = useState<AuthRole | null>()
  const [isLoggedIn, setLoggedIn] = useState<boolean>()
  const navigate = useNavigate()
  const { removeManageChannelId } = useLocalStorage()

  const { data: meData, isPending, refetch } = useGetMe()

  const queryClient = useQueryClient()
  const { mutate: callSignOut } = useMutation({
    mutationFn: apiSignOut,
    onSuccess: () => {
      queryClient.removeQueries({ queryKey: ['/v1/me'] })
      setLoggedIn(false)
      setRole(null)
      setMe(null)
      navigate(pagePath.login)
    },
  })

  useEffect(() => {
    if (isPending) {
      return
    }

    if (meData && meData.status) {
      setLoggedIn(true)
      setRole(meData.type)
      setMe(meData)
    } else {
      setLoggedIn(false)
      setRole(null)
      setMe(null)
    }

    // if (meData && meData.status === 'VERIFYING_EMAIL') {
    //   if (router.pathname !== '/signup/welcome' && router.pathname !== '/signup/verify') {
    //     router.push('/signup/welcome', undefined, { shallow: true })
    //   }
    // }
  }, [isPending, meData])

  useEffect(() => {
    if (!meData) return

    identifyUser({
      id: meData.email,
      account_type: 'youtuber',
      organization_name: meData.corpName,
    })
  }, [meData])

  const value = {
    isLoggedIn,
    me: me,
    role: role,
    actions: {
      signOut: async () => {
        await callSignOut()
        removeManageChannelId()
      },
      reloadMe: async () => {
        return refetch()
      },
    },
  } as AuthContextType

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
